import type { ArtistLanguageDto } from '@customTypes/types';
import { ArtistLanguageLevels } from '@customTypes/types';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Flex, Radio, Space, Text, TextInput, Title } from '@mantine/core';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { translate } from '@/shared/i18n/translate';
import { LBModalLayout } from '@/shared/layouts';
import { LBButton, LBCheckbox, LBLinkButton } from '@/shared/ui-kit/components';

import { useForeignLanguage } from './hooks/useForeignLanguage';

export const ForeignLanguage = ({
  onReturnSelectedValues,
  initialValues
}: {
  initialValues: ArtistLanguageDto[];
  onReturnSelectedValues: (value: ArtistLanguageDto[]) => void;
}) => {
  const { state, functions } = useForeignLanguage();
  const { formatMessage } = useIntl();
  useEffect(() => {
    state.form.setFieldValue('languages', initialValues);
  }, [initialValues]);

  const languageLevel = [
    {
      name: formatMessage({ id: 'commons.languages.A1' }),
      value: ArtistLanguageLevels.A1
    },
    {
      name: formatMessage({ id: 'commons.languages.A2' }),
      value: ArtistLanguageLevels.A2
    },
    {
      name: formatMessage({ id: 'commons.languages.B1' }),
      value: ArtistLanguageLevels.B1
    },
    {
      name: formatMessage({ id: 'commons.languages.B2' }),
      value: ArtistLanguageLevels.B2
    },
    {
      name: formatMessage({ id: 'commons.languages.C1' }),
      value: ArtistLanguageLevels.C1
    },
    {
      name: formatMessage({ id: 'commons.languages.C2' }),
      value: ArtistLanguageLevels.C2
    }
  ];

  return (
    <div>
      <LBModalLayout
        opened={state.modal.opened}
        withCloseButton={false}
        onClose={() => {}}
      >
        <Title order={2}>{translate('popups.foreignLanguage.title')}</Title>
        <Space h={32} />
        <Radio.Group
          onChange={(value) =>
            functions.setCurrentLevel(value as ArtistLanguageLevels)
          }
        >
          <Flex direction='column' gap={8}>
            {languageLevel.map((item) => (
              <Radio key={item.name} label={item.name} value={item.value} />
            ))}
          </Flex>
        </Radio.Group>
        <Space h={32} />
        <Flex gap={20} justify='end'>
          <LBLinkButton onClick={state.modal.close}>
            {translate('commons.cancel')}
          </LBLinkButton>
          <LBLinkButton
            disabled={!state.currentLevel}
            onClick={() => {
              state.form.insertListItem('languages', {
                name: state.currentLang,
                level: state.currentLevel
              });
              functions.setCurrentLevel(null);
              state.modal.close();
            }}
          >
            {translate('commons.save')}
          </LBLinkButton>
        </Flex>
      </LBModalLayout>
      <Title>{translate('commons.foreignLanguages')}</Title>
      <Space h={24} />
      <TextInput
        placeholder={formatMessage({ id: 'commons.search' })}
        rightSection={<MagnifyingGlassIcon width={24} />}
        value={state.search}
        onChange={(e) => functions.setSearch(e.target.value)}
      />
      <Space h={32} />
      <Flex direction='column' gap={8}>
        {state.form.getValues().languages.map((language) => (
          <div key={language.name}>
            <LBCheckbox
              checked
              label={language.name}
              onChange={() => {
                const formItems = state.form.getValues().languages;
                const itemIndex = formItems.findIndex(
                  (item) => item.name === language.name
                );
                state.form.removeListItem('languages', itemIndex);
              }}
            />
            <Text fz={14}>
              {translate(`commons.languages.${language.level}`)}
            </Text>
          </div>
        ))}
        {state.filteredLanguages.map((language, index) => (
          <div key={`${language.value}-${index}`}>
            <LBCheckbox
              label={language.label}
              onChange={(e) => {
                const value = e.currentTarget.checked;
                if (value) {
                  functions.setCurrentLang(language.value);
                  state.modal.open();
                }
                const formItems = state.form.getValues().languages;
                const itemIndex = formItems.findIndex(
                  (item) => item.name === language.value
                );
                state.form.removeListItem('languages', itemIndex);
              }}
            />
          </div>
        ))}
      </Flex>
      <Space h={24} />

      <Flex bottom={60} pos='sticky'>
        <LBButton
          fullWidth
          onClick={() => {
            onReturnSelectedValues(state.form.getValues().languages);
          }}
        >
          {translate('commons.save-and-continue')}
        </LBButton>
      </Flex>
    </div>
  );
};
